<script setup>
import { computed } from 'vue';
import { useClipboard } from '@vueuse/core';

const props = defineProps({
  url: {
    type: String,
    default: '',
  },
  view: {
    type: String,
    default: 'light',
    validator(value) {
      return ['dark', 'light'].includes(value);
    },
  },
});

const requestUrl = useRequestURL();

const fullUrl = computed(() => {
  if (!props.url) return requestUrl.href;
  if (props.url.startsWith('http')) return props.url;

  const origin = requestUrl.origin.replace(/\/+$/, '');
  const path = props.url.replace(/^\/+/, '');
  return `${origin}/${path}`;
});

const { copy, isSupported, copied } = useClipboard({ source: fullUrl });

const handleCopy = () => {
  copy(fullUrl.value);
};
</script>

<template>
  <div class="share-buttons">
    <SocialShare
      v-for="network in ['email', 'facebook', 'telegram']"
      :key="network"
      :network="network"
      :label="false"
      :url="fullUrl"
      :class="[
        'share-buttons__item button-icon-only',
        `button-icon-only--${view}`,
      ]"
    >
      <template #icon>
        <CIcon
          :name="view === 'dark' ? network : `${network}-black`"
          class="button-icon-only__icon"
          width="24"
          height="24"
        />
      </template>
    </SocialShare>
    <div v-if="isSupported" class="share-buttons__copy-wrapper">
      <Button
        rounded
        variant="outlined"
        size="large"
        :class="[
          'share-buttons__item button-icon-only',
          `button-icon-only--${view}`,
          `js-button-copy`,
        ]"
        @click="handleCopy"
      >
        <CIcon
          class="share-buttons__item-icon button-icon-only__icon"
          :name="view === 'dark' ? 'link' : 'link-black'"
          width="24"
          height="24"
        />
      </Button>
      <Transition name="fade">
        <div
          v-if="copied"
          class="share-buttons__tooltip body-3-regular"
          :class="`share-buttons__tooltip--${view}`"
        >
          {{ $t('share.link_copied') || 'Скопійовано!' }}
        </div>
      </Transition>
    </div>
  </div>
</template>

<style scoped lang="scss">
.share-buttons {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 16px;
  justify-content: center;
  list-style: none;
  @include media-breakpoint-up(md) {
    flex-direction: column;
  }
  @include media-breakpoint-up(xl) {
    @include responsive-spacing(gap, 16);
  }
  &__copy-wrapper {
    position: relative;
    display: flex;
  }

  &__tooltip {
    position: absolute;
    background-color: $white;
    color: $black;
    border-radius: 4px;
    box-shadow: 0px 4px 36px 0px #00000026;
    padding: 6px 12px;
    white-space: nowrap;
    z-index: 10;
    pointer-events: none;
    bottom: calc(100% + 10px);
    left: 50%;
    transform: translateX(-50%);
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(border-radius, 4);
      @include responsive-spacing(padding-top, 6);
      @include responsive-spacing(padding-bottom, 6);
      @include responsive-spacing(padding-left, 12);
      @include responsive-spacing(padding-right, 12);
      bottom: calc(100% + px-to-vw(10));
    }
    &--dark {
      background-color: $black;
      color: $white;
    }
  }
}

.fade-enter-active,
.fade-leave-active {
  transition:
    opacity 0.3s ease,
    transform 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  transform: translate(-50%, 5px);
}
</style>
